import React, { useState, useCallback } from 'react';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import classes from './SearchInput.module.scss';
import { MdClose, MdSearch } from 'react-icons/md';
import Button from 'components/atoms/Button/Button';

export interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

const SearchInput: React.FC<Props> = ({ value, onChange, placeholder, className }) => {
  const { t } = useTranslation('common');
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const resolvedPlaceholder = placeholder ? t(placeholder) : t('Search');

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const handleIconClick = () => {
    if (!isDesktop) {
      setIsExpanded(true);
      setIsClosing(false);
    }
  };

  const handleCloseClick = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
      onChange('');
    }, 300);
  };

  if (!isDesktop && !isExpanded) {
    return (
      <Button // mobile search icon button
        buttonType="secondary"
        onClick={handleIconClick}
        icon={<MdSearch />}
        iconStyle="iconOnly"
        className={clsx(classes.searchIconButton, className)}
      />
    );
  }

  return (
    <div
      className={clsx(
        classes.root,
        !isDesktop && isExpanded && classes.overlay, // mobile overlay
        isClosing && classes.closing, // mobile closing animation
      )}
    >
      {!isDesktop &&
        isExpanded && ( // mobile close icon button
          <Button
            buttonType="secondary"
            onClick={handleCloseClick}
            icon={<MdClose />}
            iconStyle="iconOnly"
            className={classes.closeButton}
            aria-label="Close search"
          />
        )}
      <div className={classes.inputWrapper}>
        <input // search input
          id="search-input"
          type="text"
          value={value}
          onChange={handleInputChange}
          placeholder={resolvedPlaceholder}
          className={classes.input}
        />
        {isDesktop && value ? (
          <button
            className={classes.inputIcon}
            onClick={() => onChange('')}
            aria-label="Clear search"
            type="button"
          >
            <MdClose />
          </button>
        ) : (
          <MdSearch className={classes.inputIcon} />
        )}
      </div>
    </div>
  );
};

export default SearchInput;
